/**
 * xlsxHelper.js — Drop-in replacement for XLSX (SheetJS) using ExcelJS
 * Provides readBinaryFile() that returns a workbook-like object with the same
 * API surface used in the codebase: SheetNames, Sheets, and a utils.sheet_to_json helper.
 */
import ExcelJS from 'exceljs';

/**
 * Read a binary string (from FileReader.readAsBinaryString) and return
 * an object shaped like XLSX.read() result.
 *
 * Options:
 *   sheetRows — if set, only read this many rows (1 = header only).
 *
 * Usage: const workbook = await readBinaryFile(data, { sheetRows: 1 });
 * Note: this is async unlike the old XLSX.read which was sync.
 */
export async function readBinaryFile(binaryStr, opts = {}) {
  const buf = binaryStringToArrayBuffer(binaryStr);
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);

  const SheetNames = wb.worksheets.map(ws => ws.name);
  const Sheets = {};

  wb.worksheets.forEach(ws => {
    const rows = [];
    const maxRow = opts.sheetRows || ws.rowCount;
    for (let r = 1; r <= Math.min(maxRow, ws.rowCount); r++) {
      const row = ws.getRow(r);
      const cells = [];
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cells[colNumber - 1] = cell.value;
      });
      rows.push(cells);
    }
    Sheets[ws.name] = rows;
  });

  return { SheetNames, Sheets };
}

/**
 * Mimics XLSX.utils.sheet_to_json(ws, { header: 1 }) — returns array of arrays.
 */
export function sheetToJson(sheetData) {
  return sheetData || [];
}

function binaryStringToArrayBuffer(binaryStr) {
  const len = binaryStr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes.buffer;
}
